import { z } from "zod";
import { Action, Subject } from "@erp/authz";
import { SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { SystemEntitySchema } from "../../utils/system-entities";
import { taskOutputSchema } from "./shared";

/**
 * Generate Task Step
 *
 * AI-powered task generation from workflow execution context and config instructions.
 * Synthesizes task fields using an LLM, then creates the task.
 * Used as both an agent tool (via ToolCatalog) and a workflow step (via StepRegistry).
 */
export const generateTaskStep = defineStepConfig({
  type: "generate_task",
  name: "Generate Task",
  description:
    "Generate and create a task using AI to synthesize fields from workflow context and instructions",
  category: "Data Access",

  inputSchema: z.object({
    sourceEntity: SystemEntitySchema.optional().describe(
      "Entity to link to this task via TASK_EXECUTION association",
    ),
  }),

  outputSchema: z.object({
    task: taskOutputSchema,
  }),

  configSchema: z.object({
    instructions: z
      .string()
      .describe(
        "Instructions for the AI on how to generate the task (e.g., 'Generate a task to run purchase matching on the invoice passed in.')",
      ),
    title: z
      .string()
      .optional()
      .describe("Optional fixed title to use instead of AI-generated title"),
    priority: z
      .enum(["low", "medium", "high", "urgent"])
      .optional()
      .describe(
        "Optional fixed priority to use instead of AI-generated priority",
      ),
    assigneeType: z
      .enum(["user", "agent"])
      .optional()
      .describe("Type of assignee for generated tasks"),
    assigneeUserId: z
      .uuid()
      .optional()
      .describe("User ID when assigneeType is user"),
    assigneeAgentDefinitionId: z
      .uuid()
      .optional()
      .describe("Agent definition assignee ID when assigneeType is agent"),
    taskNamespaceKey: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Task List key (e.g. 'SHIP') to create generated tasks in. Portable across orgs — resolved to the namespace at run time.",
      ),
    taskNamespaceId: z
      .uuid()
      .optional()
      .describe(
        "Task List (namespace) id to create generated tasks in. Prefer `taskNamespaceKey` in templates.",
      ),
  }),

  requiredPermissions: [{ action: Action.Create, subject: Subject.Task }],

  emits: [SystemEvent.TASK_CREATED] as const,
});
